import { Head, Link, router } from '@inertiajs/react'
import { useState, useCallback, useRef } from 'react'

interface TextImportProps {
  workspace: { id: number; name: string; slug: string }
  ai_configured: boolean
}

export default function TextImport({ workspace, ai_configured }: TextImportProps) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const handleSubmit = useCallback(async () => {
    if (!text.trim() || submitting) return

    setSubmitting(true)
    setError(null)
    setStatus('Submitting...')

    const csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
    try {
      const res = await fetch(`/api/${workspace.slug}/text_imports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ text }),
      })

      if (!res.ok) {
        const data = await res.json()
        setError(data.error || 'Failed to submit')
        setSubmitting(false)
        setStatus(null)
        return
      }

      const { id } = await res.json()
      setStatus('Processing...')

      pollRef.current = setInterval(async () => {
        try {
          const pollRes = await fetch(`/api/${workspace.slug}/text_imports/${id}`, {
            headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrf },
          })
          const data = await pollRes.json()

          if (data.status === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current)
            router.visit(`/${workspace.slug}/change_requests`)
          } else if (data.status === 'failed') {
            if (pollRef.current) clearInterval(pollRef.current)
            setError(data.error_message || 'Import failed')
            setSubmitting(false)
            setStatus(null)
          }
        } catch {
          // keep polling on network errors
        }
      }, 2000)
    } catch {
      setError('Network error')
      setSubmitting(false)
      setStatus(null)
    }
  }, [text, submitting, workspace.slug])

  return (
    <div className="min-h-screen bg-gray-50">
      <Head title="Import Text" />

      <nav className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link href={`/${workspace.slug}`} className="text-xl font-bold text-gray-900">
              {workspace.name}
            </Link>
          </div>
        </div>
      </nav>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-gray-900">Import Text</h2>
        <p className="mt-2 text-sm text-gray-600">
          Paste any text below. AI will analyze it and create change requests for new pages, folders, or edits to existing pages.
        </p>

        {!ai_configured && (
          <div className="mt-4 rounded-md bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              AI is not configured for this workspace. Please configure AI settings first.
            </p>
          </div>
        )}

        <div className="mt-6">
          <textarea
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            rows={16}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste your text here..."
            disabled={submitting || !ai_configured}
          />
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {status && (
          <div className="mt-4 rounded-md bg-blue-50 p-4">
            <p className="text-sm text-blue-800">{status}</p>
          </div>
        )}

        <div className="mt-6 flex items-center gap-4">
          <button
            onClick={handleSubmit}
            disabled={submitting || !text.trim() || !ai_configured}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Processing...' : 'Import'}
          </button>
          <Link href={`/${workspace.slug}`} className="text-sm font-medium text-gray-600 hover:text-gray-900">
            Cancel
          </Link>
        </div>
      </main>
    </div>
  )
}
